import React from "react";

import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import DownloadIcon from "@mui/icons-material/Download";

import Dropzone from "../Common/Dropzone";

const artworkStatuses = [
  { value: "Pending", text: "Pending" },
  { value: "Approved", text: "Approved" },
];

const ArtworkApproval = ({
  classes,
  isReadOnly,
  logoReceivedDate,
  setLogoReceivedDate,
  artworkApprovedStatus,
  setArtworkApprovedStatus,
  sampleImageEmailedDate,
  setSampleImageEmailedDate,
  logoTemplateUploadedDate,
  setLogoTemplateUploadedDate,
  artworkApprovedDate,
  setArtworkApprovedDate,
  sampleImageApproved,
  setSampleImageApproved,
  approvalNotes,
  setApprovalNotes,
  artworks,
  samples,
  setArtworkFilesToUpload,
  setSampleFilesToUpload,
  isSampleRequired,
  setIsSampleRequired,
}) => {
  const artworkImage = artworks?.find((art) => art.image_type === 2);
  const sampleImage = samples?.find((art) => art.image_type === 3);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
  const artworkImageUrl = artworkImage?.image
    ? `${API_BASE_URL}/${artworkImage.image}`
    : `${API_BASE_URL}/file/jobs/NoImageAvailable.png`;
  const sampleImageUrl = sampleImage?.image
    ? `${API_BASE_URL}/${sampleImage.image}`
    : `${API_BASE_URL}/file/jobs/NoImageAvailable.png`;

  return (
    <Paper className={classes.paper} sx={{ height: "100%" }}>
      <Grid container spacing={2}>
        {/* Part 2, Sub-part 1 */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 className={classes.detailsHeader}>
              <b>Artwork Approved</b>
            </h3>
            {artworkImage && (
              <IconButton component="a" href={artworkImageUrl} download>
                <DownloadIcon />
              </IconButton>
            )}
          </Box>
          {!isReadOnly && <Box sx={{ minHeight: 42 }} /> /* Placeholder for alignment */}
          {!isReadOnly && <Dropzone handleUploadFile={setArtworkFilesToUpload} imageOnly={true} />}
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <img
              src={artworkImageUrl}
              alt="Current Artwork"
              style={{ maxHeight: "100px", maxWidth: "100%", border: "1px solid #ddd", padding: "4px", display: "block" }}
            />
          </Box>
          <InputLabel className="inputFormCustomLabel">Logo Received</InputLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
              value={logoReceivedDate}
              onChange={(date) => setLogoReceivedDate(date)}
              renderInput={(params) => <TextField {...params} fullWidth variant="outlined" size="small" />}
              inputFormat="DD/MM/YYYY"
              readOnly={isReadOnly}
            />
          </LocalizationProvider>
        </Grid>

        {/* Part 2, Sub-part 2 */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 className={classes.detailsHeader}>
              <b>Sample Approved</b>
            </h3>
            {sampleImage && (
              <IconButton component="a" href={sampleImageUrl} download>
                <DownloadIcon />
              </IconButton>
            )}
          </Box>
          {!isReadOnly && (
            <FormControlLabel
              control={<Switch checked={isSampleRequired} onChange={(e) => setIsSampleRequired(e.target.checked)} name="isSampleRequired" />}
              label="Sample Required"
            />
          )}
          {!isReadOnly && <Dropzone handleUploadFile={setSampleFilesToUpload} imageOnly={true} />}
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <img
              src={sampleImageUrl}
              alt="Current Sample"
              style={{ maxHeight: "100px", maxWidth: "100%", border: "1px solid #ddd", padding: "4px", display: "block" }}
            />
          </Box>
          <InputLabel className="inputFormCustomLabel">Artwork Approval Sent</InputLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
              value={logoTemplateUploadedDate}
              onChange={(date) => setLogoTemplateUploadedDate(date)}
              renderInput={(params) => <TextField {...params} fullWidth variant="outlined" size="small" />}
              inputFormat="DD/MM/YYYY"
              readOnly={isReadOnly}
            />
          </LocalizationProvider>
        </Grid>
        {/* Combined Fields Row */}
        <Grid item xs={12} sm={6}>
          <InputLabel className="inputFormCustomLabel">Artwork Approved Status</InputLabel>
          {isReadOnly ? (
            (() => {
              const status = artworkStatuses.find((s) => s.value === artworkApprovedStatus);
              return (
                <TextField fullWidth variant="outlined" size="small" value={status ? status.text : ""} InputProps={{ readOnly: true }} />
              );
            })()
          ) : (
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              value={artworkApprovedStatus}
              onChange={(e) => setArtworkApprovedStatus(e.target.value)}
            >
              {artworkStatuses.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.text}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel className="inputFormCustomLabel">Artwork Approved Date</InputLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
              value={artworkApprovedDate}
              onChange={(date) => setArtworkApprovedDate(date)}
              renderInput={(params) => <TextField {...params} fullWidth variant="outlined" size="small" />}
              inputFormat="DD/MM/YYYY"
              readOnly={isReadOnly}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel className="inputFormCustomLabel">Sample Image Emailed</InputLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
              value={sampleImageEmailedDate}
              onChange={(date) => setSampleImageEmailedDate(date)}
              renderInput={(params) => <TextField {...params} fullWidth variant="outlined" size="small" />}
              inputFormat="DD/MM/YYYY"
              readOnly={isReadOnly}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel className="inputFormCustomLabel">Sample Image Approved</InputLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
              value={sampleImageApproved}
              onChange={(date) => setSampleImageApproved(date)}
              renderInput={(params) => <TextField {...params} fullWidth variant="outlined" size="small" />}
              inputFormat="DD/MM/YYYY"
              readOnly={isReadOnly}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <InputLabel className="inputFormCustomLabel">Approval Notes</InputLabel>
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            size="small"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            InputProps={{ readOnly: isReadOnly }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default React.memo(ArtworkApproval);
